import { useState, useEffect } from "react";
import {ContentService} from "../api/services.gen"
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from 'dayjs';
import { IMG_VALIDATE, NEWS_STATUS_OPTIONS } from '../constants/index';
import { getMinPublicTime } from "../utils/util";
import i18n from 'i18next';
import { isValidTagString } from "../utils/validation"
import { useDispatch, useSelector } from "react-redux";
import { TypeOptions } from "../pages/companyJob/components/TypeOptions";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { setIsFormDirty, setShowLeaveConfirm } from "./app";
import { setIsOpenSearchTagsPanel } from "reducers/talentTag";
import { setIsUploadImageError, setErrorMainInageState } from "reducers/companyContent";

type ErrorState = {
  image: string,
  title: string,
  tags?: string,
  type: string,
  contents: string,
  status: string,
  public_schedule_date: string,
  public_schedule_hour: string,
  public_schedule_minute: string,
}
type News = {
  title: string,
  tags: string,
  contents: string,
  type: string,
  images: (File|Blob) | null,
  currentImage: string | null,
  status?: number,
  is_public_scheduled: boolean,
  public_schedule_date: string,
  public_schedule_hour: string,
  public_schedule_minute: string,
  do_send_notification_email: boolean,
  talent_tags: string,
  tags_condition: {id: number, name: string},
}

const INITIAL_NEWS: News = {
  title: "",
  tags: "",
  contents: "",
  type: "",
  images: null,
  currentImage: null,
  status: null,
  is_public_scheduled: false,
  public_schedule_date: "",
  public_schedule_hour: "",
  public_schedule_minute: "",
  do_send_notification_email: true,
  talent_tags: "",
  tags_condition: null,
}

const getScheduleDate = (date, hour, minute): any => {
  const scheduled = new Date(date);
  scheduled.setHours(hour);
  scheduled.setMinutes(minute);
  scheduled.setSeconds(0);
  scheduled.setMilliseconds(0);
  return scheduled;
}

const validateSchedulePost = (formData: any): { hasError: boolean; errorState: ErrorState } => {
  let hasError = false;
  let errorState: ErrorState = {};

  if (formData.is_public_scheduled) {
    if (!formData.public_schedule_date) {
      hasError = true;
      errorState.public_schedule_date = i18n.t('MSG_GLOBAL_PUBLISH_SCHEDULE_DATE_REQUIRED');
    }
    if (formData.public_schedule_hour || formData.public_schedule_minute) {
      if(formData.public_schedule_hour < 0 || formData.public_schedule_hour > 23) {
        hasError = true;
        errorState.public_schedule_hour = i18n.t('MSG_GLOBAL_HOUR_MINUTE_RANGE');
      }
      if(formData.public_schedule_minute < 0 || formData.public_schedule_minute > 59) {
        hasError = true;
        errorState.public_schedule_minute = i18n.t('MSG_GLOBAL_HOUR_MINUTE_RANGE');
      }
    }
    if(!formData.public_schedule_hour) {
      hasError = true;
      errorState.public_schedule_hour = i18n.t('MSG_GLOBAL_HOUR_REQUIRED');
    }
    if (!formData.public_schedule_minute) {
      hasError = true;
      errorState.public_schedule_minute = i18n.t('MSG_GLOBAL_MINUTE_REQUIRED');
    }
    if (formData.public_schedule_date && formData.public_schedule_hour && formData.public_schedule_minute) {
      const publicTimeErrorMsg = i18n.t('MSG_GLOBAL_PUBLISH_SCHEDULE_MIN_ONE_HOUR');
      const scheduled = getScheduleDate(formData.public_schedule_date, formData.public_schedule_hour, formData.public_schedule_minute);
      const nowPlus1Hour = getMinPublicTime();

      if (scheduled < nowPlus1Hour) {
        hasError = true;
        errorState.public_schedule_date = errorState.public_schedule_hour = errorState.public_schedule_minute = publicTimeErrorMsg;
      }
    }
  }

  return { hasError, errorState };
};


const validateForm = (formData: News) => {
  let errorState: ErrorState = {} as ErrorState
  let hasError = false

  if (!formData.images && !formData.currentImage){
    hasError = true;
    errorState = {
      ...errorState,
      image: "*画像は必須です。"
    }
  }

  if (!formData.title || 
    formData.title.trim() == ""
  ){
    hasError = true;
    errorState = {
      ...errorState,
      title: "タイトルは必須です。"
    }
  }

  if (!formData.contents || formData.contents.trim() == ""){
      hasError = true;
      errorState = {
        ...errorState,
        contents: "本文は必須です。"
      }
  }

  if (!formData.type) {
      hasError = true;
      errorState = {
        ...errorState,
        type: "ジャンルは必須です。"
      }
  }

  if(formData.tags){
    if (!isValidTagString(formData.tags)) {
      hasError = true;
      errorState = {
        ...errorState,
        tags: i18n.t('MSG_GLOBAL_TAG_SEPARATOR_INVALID')
      }
    }
  }

  if (formData.is_public_scheduled) {
    const { hasError: hasErrorSchedule, errorState: errorStateSchedule } = validateSchedulePost(formData);

    if (hasErrorSchedule) {
      hasError = true;
      errorState = {
        ...errorState,
        ...errorStateSchedule,
      };
    }
  }

  if (!formData.talent_types || !formData?.talent_types?.some(item => item.value)) {
    hasError = true;
    errorState = {
      ...errorState,
      talentType: "タレントの種別はひとつ以上選択してください"
    }
  }

  return {hasError, errorState}
}

const useCustomHook = () => {
  const location = useLocation();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const {isFormDirty, showLeaveConfirm} = useSelector((state: any) => state.app);
  const [news, setNews] = useState<News>(INITIAL_NEWS as News)
  const [image, setImage] = useState<string>('');
  const [isOver, setIsOver] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [loadingNews, setLoadingNews] = useState<boolean>(false);
  const [type, setType] = useState<any>(null);
  const [publishStatus, setPublishStatus] = useState<any>(null);
  const [publicScheduleDate, setPublicScheduleDate] = useState<any>(null);
  const [reservedHour, setReservedHour] = useState<any>(null);
  const [reservedMinute, setReservedMinute] = useState<any>(null);
  const [isPublishReserved, setIsPublishReserved] = useState<boolean>(false);
  const [notifyTalent, setNotifyTalent] = useState<boolean>(true);
  const [errorState, setErrorState] = useState<ErrorState>({} as ErrorState);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const navigate = useNavigate();
  const publicTimeErrorMsg = i18n.t('MSG_GLOBAL_PUBLISH_SCHEDULE_MIN_ONE_HOUR');
  const hourMinuteRangeInvalidMsg = i18n.t('MSG_GLOBAL_HOUR_MINUTE_RANGE');
  const {talentTagOptions} = useSelector((state: any) => state.companyTalent);
  const {isUploadImageError} = useSelector((state: any) => state.companyContent);
  const { talentTypeOptions } = TypeOptions();
  const [talentType, setTalentType] = useState<any>(null);
  const [tagLogicTypes, setTagLogicTypes] = useState<any>(null);
  const [isLeavePage, setIsLeavePage] = useState(false);
  const [urlLeave, setUrlLeave] = useState('');

  const isFromContentRanking = location.state?.isFromContentRanking;

  const markDirty = () => {
    if (!isFormDirty) dispatch(setIsFormDirty(true));
  };

  const handleNavigateBack = () => {
    if (isFormDirty) {
      dispatch(setShowLeaveConfirm(true));
    } else {
      navigate(-1);
    }
  };

  const handleGoToTagList = () => {
    if(isFormDirty) {
      setIsLeavePage(true)
      setUrlLeave('/company/talent/tags')
    } else {
      dispatch(setIsOpenSearchTagsPanel(false)); 
      navigate('/company/talent/tags')
    }
  };
  
  useEffect(() => {
    let isUpdate = talentTypeOptions && talentTypeOptions.length > 0 && talentType == null;
    if (isUpdate) setTalentType(talentTypeOptions);
  }, [talentTypeOptions]);

  const validateFile = (blobFile: any) => {
    setErrorState({ ...errorState, image: "" });
  
    const dispatchErrorAsync = (msg: string) => {
      handleResetImage();
      setTimeout(() => {
        dispatch(setErrorMainInageState(msg));
      }, 0);
    };
  
    if (!blobFile || (blobFile.type !== 'image/png' && blobFile.type !== 'image/jpeg')) {
      dispatchErrorAsync("ファイル形式がPNG、JPG、JPEG形式ではありません。");
      return;
    }
  
    if (blobFile.size > IMG_VALIDATE.THUMB_MAX_FILE_SIZE) {
      dispatchErrorAsync("選択した画像サイズは制限（8MB）を超えています。制限内の画像を指定してください。");
      return;
    }
  
    const img = new Image();
    img.onload = () => {
      if (img.width > IMG_VALIDATE.THUMB_MAX_WIDTH || img.height > IMG_VALIDATE.THUMB_MAX_HEIGHT) {
        dispatchErrorAsync("解像度が 横 :1980px、 縦 :720px以内のファイルを選択してください。");
      } else {
        setNews({
          ...news,
          "images": blobFile
          });
        setImage(URL.createObjectURL(blobFile));
      }
    };

    img.src = URL.createObjectURL(blobFile);
  }

  const handleChangeFile = (e: any) => {
    dispatch(setErrorMainInageState(""))
    markDirty();
    if (!e.target.files) {
      return
    }
    const blobFile = e.target.files[0];
    validateFile(blobFile);
  }

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setIsOver(false);
  };

  const handleDrop = (e: any) => {
    dispatch(setErrorMainInageState(""))
    e.preventDefault();
    setIsOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const droppedFile = e.dataTransfer.files[0];
        // Use FileReader to read file content
        validateFile(droppedFile);
    }
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleTypeChange = (option: any) => {
    markDirty();
    setType(option)
    setNews({
      ...news,
      "type": option.id
    })
    setErrorState({
      ...errorState,
      type: ""
    })
  }

  const handlePublishStatusChange = (option: any) => {
    markDirty();
    setPublishStatus(option)
    setNews({
      ...news,
      status: option.id
    })
    setErrorState({
      ...errorState,
      status: ""
    })

    if (option.value == NEWS_STATUS_OPTIONS.PRIVATE) {
      handleChangePublishReserved(false);
      setErrorState({
        ...errorState,
        public_schedule_date: "",
        public_schedule_hour: "",
        public_schedule_minute: "",
      })
    }
  }

  const handlePublicScheduleDateChange = (value: any) => {
    setPublicScheduleDate(value)
    setNews({
      ...news,
      public_schedule_date: value
    })
    setErrorState({
      ...errorState,
      public_schedule_date: ""
    })
  }

  const handleReservedHourChange = (value: any) => {
    setReservedHour(value);
    setNews({
      ...news,
      public_schedule_hour: value
    })
  }

  const handleReservedMinuteChange = (value: any) => {
    setReservedMinute(value);
    setNews({
      ...news,
      public_schedule_minute: value
    })
  }

  const handleChangeTalentType = (index: number) => {
    markDirty();
    const updatedTalentType = talentType.map((item, idx) =>
      idx === index ? { ...item, value: !item.value } : item
    );
    setTalentType(updatedTalentType);

    setNews({ ...news, talent_types: updatedTalentType });
  };

  const handleNotifyTalentChange = (checked: boolean) => {
    setNotifyTalent(checked);
    setNews({
      ...news,
      do_send_notification_email: checked
    })
  }

  const handleChange = (e:any, name: string) => {
    markDirty();
    setNews({
      ...news,
      [name]: e.target.value
    })
    setErrorState({
      ...errorState,
      [name]: ""
    })
  }

  const initializePublicSchedule = (scheduleDate: any) => {
    let date: any, hour: any, minute: any;
    date = hour = minute = null;

    if (scheduleDate) {
      const originalDatetime = dayjs(scheduleDate)
      date = originalDatetime.format('YYYY-MM-DD');
      hour = originalDatetime.format('HH');
      minute = originalDatetime.format('mm');
    }
    setPublicScheduleDate(date);
    setReservedHour(hour ? hour : "");
    setReservedMinute(minute ? minute : "");

    return {date, hour, minute};
  }

  const handleChangeContent = (value: any, name: string) => {
    markDirty();

    let valueHandled = value
    if(isUploadImageError && name == 'contents') {
      valueHandled = value.replace(/!\[[^\]]*\]\(\)/g, "");
      dispatch(setIsUploadImageError(false))
    }

    setNews({
      ...news,
      [name]: valueHandled
    })
    setErrorState({
      ...errorState,
      [name]: ""
    })
  }

  const handleSetNews = (data: any) => {
    setNews(data)
    setImage(data.images);
    setType(data.news_type_display)
    setPublishStatus(data.news_status_display)
    initializePublicSchedule(data.public_schedule_date)
  }

  const handleSetNewsWithEdit = (data: any) => {
    setImage(data.images);
    setType(data.news_type_display);
    setPublishStatus(data.news_status_display);
    setIsPublishReserved(data.is_public_scheduled)
    setNotifyTalent(data.do_send_notification_email)
    const updatedTalentTypes = talentTypeOptions.map((item: any) => ({
      ...item,
      value: (data.talent_types || []).includes(item.name)
    }));

    setTalentType(updatedTalentTypes);    
    const {date, hour, minute} = initializePublicSchedule(data.public_schedule_date)
    
    setNews({
      ...data,
      "images": null,
      "currentImage": data.images,
      "public_schedule_date": date,
      "public_schedule_hour": hour,
      "public_schedule_minute": minute,
      "news_tags": talentTagOptions?.filter(item => (data.news_tags || []).includes(item.id)),
      "tags_condition": tagLogicTypes?.find(item => item.id == data.tag_logic),
      "talent_types": updatedTalentTypes
    })
  }

  const handleSetTagLogicTypes = (data: any) => {
    setTagLogicTypes(data)
  }
  
  const handleResetImage = () => {
    setNews({
      ...news,
      "images": null,
      "currentImage": null,
    })
    setImage("");
  }

  const imageUrlToBlobFile = async (url: string): Promise<File> => {
    const filename = url.split('/').pop() || 'image.jpg';
    const response = await fetch(url);
    const blob = await response.blob();
    const fileType = blob.type || 'image/jpeg';
    const file = new File([blob], filename, { type: fileType });
    return file;
  };

  const handleChangePublishReserved = (checked: any) => {
    markDirty();
    setIsPublishReserved(checked);
    setNews({
      ...news,
      "is_public_scheduled": checked,
    })
  }

  const displayError = (event: any, hasError: any, errorState: any) => {
    if (hasError) {
      setLoadingNews(false);
      setErrorState(errorState);
      event.preventDefault();
      return false;
    }
  }

  const handleErrorResponseContent = (error: any, action: any) => {
    setLoadingNews(false);
    if (error?.status === 401) {
      window.location.assign(window.location.href);
      return;
    }
    setErrorMsg(i18n.t('MSG_GLOBAL_INTERNAL_SERVER_ERROR'));
    return;
  }
  
  const handleFormSubmit = async (event: any) => {
    setErrorMsg("");
    setSuccessMsg("");
    
    const {hasError, errorState} = validateForm(news);
    if (hasError) return displayError(event, hasError, errorState);
    else setErrorState({});

    setLoadingNews(true);

    try {
      let scheduleDate = news.public_schedule_date || null;
      if (news.public_schedule_date) {
        const localDate = getScheduleDate(news.public_schedule_date, news.public_schedule_hour, news.public_schedule_minute);
        scheduleDate = localDate.toISOString();
      }

      const result = await ContentService.contentCreateCreate({formData:{
        images: news.images,
        title: news.title,
        contents: news.contents,
        type: news.type,
        status: publishStatus.id,
        is_public_scheduled: news.is_public_scheduled,
        public_schedule_date: scheduleDate,
        do_send_notification_email: news.do_send_notification_email,
        tags: news.tags,
        tag_logic: news.tags_condition.id,
        news_tags: news?.news_tags ? news?.news_tags.map(item => item.id).join(',') : '',
        talent_types: news?.talent_types.filter(item => item.value).map(item => item.name).join(',')
      }})
      if(result.success){
        setFormSubmitted(true)
        navigate("/company/content", { state: { successMsg: result.message } })
      }
      else{
        setLoadingNews(false);
        setErrorMsg(result.message)
      }
    } catch(error: any){      
      event.preventDefault()
      return handleErrorResponseContent(error);
    }
  };
  const handleFormEdit = async (event: any, id: number) => {
    setLoadingNews(true);
    setErrorMsg("");
    setSuccessMsg("");
    
    const {hasError, errorState} = validateForm(news);
    if (hasError) return displayError(event, hasError, errorState);
    else setErrorState({});
    
    try {
      let scheduleDate = news.public_schedule_date || null;
      if (news.public_schedule_date) {
        const localDate = getScheduleDate(news.public_schedule_date, news.public_schedule_hour, news.public_schedule_minute);
        scheduleDate = localDate.toISOString();
      }

      const data = {
          images: news.images,
          title: news.title,
          contents: news.contents,
          type: news.type,
          status: publishStatus.id,
          is_public_scheduled: news.is_public_scheduled,
          public_schedule_date: scheduleDate,
          do_send_notification_email: news.do_send_notification_email,
          public_schedule_date_history: dayjs(getScheduleDate(news.public_schedule_date, news.public_schedule_hour, news.public_schedule_minute)).format('YYYY-MM-DD HH:mm'),
          tags: news.tags,
          tag_logic: news.tags_condition?.id,
          news_tags: news.news_tags ? news.news_tags.map(item => item.id).join(',') : '',
          talent_types: talentType ? talentType.filter(item => item.value).map(item => item.name).join(',') : '',
        }
      const result = await ContentService.contentDetailUpdate({ formData: data, id: id })

      if(result.success) {
        if(!result.data.is_public && !result.data.is_public_scheduled) {
          handleSetNewsWithEdit(result.data);
          setSuccessMsg(result.message);
          setLoadingNews(false);
          dispatch(setIsFormDirty(false));
          dispatch(setShowLeaveConfirm(false));
          return;
        }
        setFormSubmitted(true);
        
        if(isFromContentRanking) {
          navigate("/company/content-ranking", { state: { successMsg: result.message } })
        } else {
          navigate("/company/content", { state: { successMsg: result.message } })
        }
      } else {
        setLoadingNews(false);
        setErrorMsg(result.message)
      }
    } catch(error: any){
      event.preventDefault()
      return handleErrorResponseContent(error);
    }
  };

  const handleDeleteNews = async (event: any, id: number) => {
    setLoadingNews(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const result = await ContentService.contentDetailDestroy({
        id: Number(id)
      })
      if(result.success){
        setFormSubmitted(true)
        navigate("/company/content", { state: { successMsg: result.message } })
      }
      else{
        setLoadingNews(false);
        setErrorMsg(result.message)
      }
    } catch(error: any){
      event.preventDefault()
      return handleErrorResponseContent(error);
    }
  };

  const handleCloseLeaveConfirm = () => {
    dispatch(setShowLeaveConfirm(false));
  }

  const handleCloseLeavePageConfirm = () => {
    setIsLeavePage(false);
  }

  return {
      handleChangeFile,
      validateFile,
      imageUrlToBlobFile,
      image,
      isOver,
      type,
      publishStatus,
      reservedHour,
      reservedMinute,
      isPublishReserved,
      news,
      errorState,
      formSubmitted,
      errorMsg,
      successMsg,
      loadingNews,
      notifyTalent,
      talentType,
      handleCloseLeaveConfirm,
      handleNavigateBack,
      handleGoToTagList,
      handleCloseLeavePageConfirm,
      markDirty,
      handleDragLeave,
      handleDrop,
      handleDragOver,
      handleTypeChange,
      handleChange,
      handleChangeContent,
      handleChangeTalentType,
      handleFormSubmit,
      navigate,
      handleResetImage,
      handleSetNews,
      handleFormEdit,
      handleSetNewsWithEdit,
      handlePublishStatusChange,
      handleChangePublishReserved,
      handleReservedHourChange,
      handleReservedMinuteChange,
      handleNotifyTalentChange,
      setErrorMsg,
      setSuccessMsg,
      handleDeleteNews,
      publicScheduleDate,
      handlePublicScheduleDateChange,
      publicTimeErrorMsg,
      hourMinuteRangeInvalidMsg,
      tagLogicTypes,
      handleSetTagLogicTypes,
      isLeavePage,
      urlLeave,
    } 
}

export default useCustomHook; 